import { ApiResponseError, type ApiClient } from "@shell/auth/apiClient";
import { exportListResponseSchema } from "../common/schemas";
import {
    addAssetToWorkspaceResponseSchema,
    bulkImportWorkspacesResponseSchema,
    reassignWorkspaceAssetsResponseSchema,
    workspaceListResponseSchema,
    workspaceMutationResponseSchema,
    workspaceViewSchema,
    type AddAssetToWorkspacePayload,
    type AddAssetToWorkspaceResult,
    type BulkImportWorkspacesPayload,
    type BulkImportWorkspacesResult,
    type ReassignWorkspaceAssetsResult,
    type WorkspaceUpsertPayload,
    type WorkspaceView,
} from "./types";

interface SearchParams {
    readonly q?: string;
    readonly sageOfficeId?: string;
    readonly type?: string;
    readonly includeDeleted?: boolean;
    readonly floorplanOnly?: boolean;
}

/**
 * Resolves a single workspace id to its view. Returns null when the id is
 * empty or the API has no record (the SPA treats this as a benign
 * "unknown" state and falls back to displaying the raw id wherever the
 * view would have appeared). `includeDeleted=true` so assignment detail
 * panels still render a friendly label after a workspace has been
 * soft-deleted — the assignment record outlives its target.
 */
export async function lookupWorkspace(
    client: ApiClient,
    id: string,
): Promise<WorkspaceView | null> {
    const trimmed = id.trim();
    if (trimmed === "") return null;

    try {
        const raw = await client.getJson<unknown>(
            `/api/workspaces/${encodeURIComponent(trimmed)}?includeDeleted=true`,
        );
        return workspaceMutationResponseSchema.parse(raw).workspace;
    } catch (error) {
        if (error instanceof ApiResponseError && error.status === 404) {
            return null;
        }
        throw error;
    }
}

export async function searchWorkspaces(
    client: ApiClient,
    params: SearchParams,
): Promise<readonly WorkspaceView[]> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.sageOfficeId !== undefined && params.sageOfficeId.length > 0)
        search.set("sageOfficeId", params.sageOfficeId);
    if (params.type !== undefined && params.type.length > 0) search.set("type", params.type);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");
    if (params.floorplanOnly === true) search.set("floorplanOnly", "true");

    const path = search.size > 0 ? `/api/workspaces?${search.toString()}` : "/api/workspaces";
    const raw = await client.getJson<unknown>(path);
    return workspaceListResponseSchema.parse(raw).results;
}

export async function exportWorkspaces(
    client: ApiClient,
    params: SearchParams,
): Promise<{ readonly results: readonly WorkspaceView[]; readonly truncated: boolean }> {
    const search = new URLSearchParams();
    if (params.q !== undefined && params.q.length > 0) search.set("q", params.q);
    if (params.sageOfficeId !== undefined && params.sageOfficeId.length > 0)
        search.set("sageOfficeId", params.sageOfficeId);
    if (params.type !== undefined && params.type.length > 0) search.set("type", params.type);
    if (params.includeDeleted === true) search.set("includeDeleted", "true");
    if (params.floorplanOnly === true) search.set("floorplanOnly", "true");

    const path =
        search.size > 0
            ? `/api/exports/workspaces?${search.toString()}`
            : "/api/exports/workspaces";
    const raw = await client.getJson<unknown>(path);
    return exportListResponseSchema(workspaceViewSchema).parse(raw);
}

export async function createWorkspace(
    client: ApiClient,
    payload: WorkspaceUpsertPayload,
): Promise<WorkspaceView> {
    // Spread the entire payload so newly added optional fields
    // (e.g. friendlyName) flow through without each new field
    // requiring a touch here. The backend ignores anything it
    // doesn't recognize, so this stays safe.
    const response = await client.fetch("/api/workspaces", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return await readMutationResponse(response);
}

export async function updateWorkspace(
    client: ApiClient,
    payload: WorkspaceUpsertPayload,
): Promise<WorkspaceView> {
    const response = await client.fetch(`/api/workspaces/${encodeURIComponent(payload.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            workspaceId: payload.id,
            sageOfficeId: payload.sageOfficeId,
            type: payload.type,
            name: payload.name,
            friendlyName: payload.friendlyName,
            floorplanElementId: payload.floorplanElementId,
        }),
    });
    return await readMutationResponse(response);
}

export async function deleteWorkspace(client: ApiClient, id: string): Promise<void> {
    const response = await client.fetch(`/api/workspaces/${encodeURIComponent(id)}`, {
        method: "DELETE",
    });
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
}

export async function restoreWorkspace(client: ApiClient, id: string): Promise<WorkspaceView> {
    const response = await client.fetch(`/api/workspaces/${encodeURIComponent(id)}/restore`, {
        method: "POST",
    });
    return await readMutationResponse(response);
}

/**
 * Workspace-anchored "add asset" call. Hits the atomic backend endpoint
 * that creates the asset and the workspace assignment in a single Cosmos
 * transactional batch — see `AddAssetToWorkspaceEndpoint` on the API.
 *
 * The caller supplies BOTH ids (asset + assignment) so the operation is
 * idempotent: a phone losing connectivity mid-save can replay without
 * risking a duplicate. A 409 from the server therefore means the prior
 * call already succeeded; the SPA treats that as success after refetching.
 */
export async function addAssetToWorkspace(
    client: ApiClient,
    workspaceId: string,
    payload: AddAssetToWorkspacePayload,
): Promise<AddAssetToWorkspaceResult> {
    // The route id is duplicated into the body deliberately. The backend
    // DTO marks `WorkspaceId` as `required`; System.Text.Json enforces
    // that on body deserialization *before* FastEndpoints' route-value
    // binding has a chance to populate it, so omitting it here yields
    // a 400 "missing required properties: workspaceId" even though the
    // value is sitting right there in the URL. Sending both keeps STJ
    // happy; route binding still wins on the server.
    const response = await client.fetch(
        `/api/workspaces/${encodeURIComponent(workspaceId)}/assets`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                workspaceId,
                asset: payload.asset,
                assignmentId: payload.assignmentId,
            }),
        },
    );

    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }

    const raw = (await response.json()) as unknown;
    return addAssetToWorkspaceResponseSchema.parse(raw);
}

/**
 * "Reassign All": moves every open asset assignment at `sourceWorkspaceId`
 * to `targetWorkspaceId`. Each asset moves atomically on the server, but
 * the call as a whole does not — a 200 can carry per-asset failures, so
 * callers must read `failed` / `results` rather than trusting the status.
 * Re-issuing after a partial failure only retries what is still at the
 * source.
 */
export async function reassignWorkspaceAssets(
    client: ApiClient,
    sourceWorkspaceId: string,
    targetWorkspaceId: string,
): Promise<ReassignWorkspaceAssetsResult> {
    // Route id duplicated into the body for the same reason as
    // `addAssetToWorkspace` (STJ `required` runs before route binding).
    const response = await client.fetch(
        `/api/workspaces/${encodeURIComponent(sourceWorkspaceId)}/assets/reassign`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ workspaceId: sourceWorkspaceId, targetWorkspaceId }),
        },
    );
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return reassignWorkspaceAssetsResponseSchema.parse(raw);
}

async function readMutationResponse(response: Response): Promise<WorkspaceView> {
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return workspaceMutationResponseSchema.parse(raw).workspace;
}

/**
 * Bulk-import call. Hits `POST /api/workspaces/bulk` with the parsed
 * rows as a JSON array. The server processes each row independently,
 * so a 200 response can include per-row failures — callers should
 * inspect `result.failed` and `result.results` rather than relying on
 * the HTTP status alone.
 *
 * Idempotency: rows carry client-generated UUIDs. Re-calling with the
 * same `items` is safe — already-created rows come back with
 * `outcome: "skipped"` instead of a duplicate.
 */
export async function bulkImportWorkspaces(
    client: ApiClient,
    payload: BulkImportWorkspacesPayload,
): Promise<BulkImportWorkspacesResult> {
    const response = await client.fetch("/api/workspaces/bulk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const body = await response.text();
        throw new ApiResponseError(response.status, response.statusText, body);
    }
    const raw = (await response.json()) as unknown;
    return bulkImportWorkspacesResponseSchema.parse(raw);
}
